import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheckCircle, FiLoader, FiAlertCircle } from 'react-icons/fi';
import { subscriptionsAPI } from '../services/api';
import { setStorageItem } from '../utils/helpers';
import { STORAGE_KEYS } from '../utils/constants';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Procesando tu pago...');
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    const completeRegistration = async () => {
      try {
        // Obtener payment_id del sessionStorage
        const paymentId = sessionStorage.getItem('pending_payment_id');

        if (!paymentId) {
          setStatus('error');
          setMessage('No se encontró el ID de pago. Por favor, inicia el proceso de registro nuevamente.');
          return;
        }

        // Capturar el pago - El backend crea establecimiento, usuario y suscripción automáticamente
        setMessage('Verificando tu pago...');
        const captureResult = await subscriptionsAPI.capturePayment(paymentId);

        // El backend retorna todo listo, incluyendo el token de acceso
        setMessage('Configurando tu cuenta...');

        // Guardar token y datos del usuario
        setStorageItem(STORAGE_KEYS.TOKEN, captureResult.access_token);

        // Guardar información básica del usuario
        const userData = {
          id: captureResult.user_id,
          establishment_id: captureResult.establishment_id,
          subscription_id: captureResult.subscription_id
        };
        setStorageItem(STORAGE_KEYS.USER, JSON.stringify(userData));

        // Limpiar sessionStorage
        sessionStorage.removeItem('pending_payment_id');

        // Mostrar éxito
        setStatus('success');
        setMessage('¡Tu suscripción está activa!');
        setSubscription(captureResult);

        // Redirigir al dashboard después de 2 segundos
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);

      } catch (error) {
        console.error('Error completing registration:', error);
        setStatus('error');
        setMessage(
          error.response?.data?.detail ||
          'Hubo un error al procesar tu pago. Por favor, contacta con soporte.'
        );
      }
    };

    completeRegistration();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
        {/* Processing */}
        {status === 'processing' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiLoader className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Procesando...
            </h2>
            <p className="text-gray-600 mb-6">
              {message}
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <FiLoader className="w-4 h-4 animate-spin" />
              <span>Por favor espera, esto puede tardar unos momentos</span>
            </div>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiCheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              ¡Bienvenido a Teonila HouseKeeping Management!
            </h2>
            <p className="text-gray-600 mb-6">
              Tu pago se ha procesado correctamente y tu suscripción está activa.
            </p>

            {subscription && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 mb-6">
                <div className="text-sm text-gray-700 space-y-2">
                  <p>
                    <strong>Período de prueba:</strong> 7 días gratis
                  </p>
                  <p>
                    <strong>Fin de prueba:</strong>{' '}
                    {subscription.trial_end
                      ? new Date(subscription.trial_end).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                      : 'N/A'}
                  </p>
                  <p className="text-xs text-blue-700 mt-3">
                    Puedes cancelar durante el período de prueba y te devolveremos el 100% del importe.
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <FiLoader className="w-4 h-4 animate-spin" />
              <span>Redirigiendo al panel de control...</span>
            </div>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiAlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Hubo un Problema
            </h2>
            <p className="text-gray-600 mb-6">
              {message}
            </p>

            <div className="space-y-3">
              <button
                onClick={() => navigate('/')}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-semibold"
              >
                Volver al Inicio
              </button>
              <button
                onClick={() => window.location.href = 'mailto:info@teonila.com'}
                className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Contactar Soporte
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
